import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Divider,
  InputAdornment, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/ErrorOutline'
import SearchIcon from '@mui/icons-material/Search'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { useDispatch, useSelector } from 'react-redux'
import {
  updateAdminConfig, validateAdminSql, clearValidation,
  fetchAdminCatalog, fetchAdminCatalogColumns, fetchAdminSqlFunctions,
  selectAdminValidation, selectAdminSaving,
  selectAdminCatalog, selectAdminCatalogColumnsMap, selectAdminCatalogLoading,
  selectAdminSqlFunctions,
} from '../../reducers/Beacon4PerformanceNetworkOverviewAdminReducer'
import {
  COLUMN_FIELDS, DRAG_MIME, catalogLabel, functionDragText, functionInsertion,
  computeAutoFill, spliceToken, autoFillLabel,
} from './Beacon4PerformanceNetworkOverviewAdminSourceHelpers'

// Per-field widths for this screen's wrap layout. The field list itself is
// shared with the metadata dialog, which lays them out on a grid instead.
const FIELD_WIDTHS = {
  dateCol: 140, sideCol: 140, polygonCol: 160, jvidCol: 170,
  mycomCol: 150, vendorCol: 150, importDateCol: 220,
}

// Draggable width of the schema rail.
//
// 260px was too narrow for the real column names in these views — things like
// S12_2G_Availability_Universal_A+Pref wrapped onto two lines and became hard
// to scan, and truncating them isn't an option when the whole point is to read
// the name and drop it into a formula. Rather than pick a new fixed width that
// is wrong for a different view, the rail is resizable and remembered.
const RAIL_MIN = 200
const RAIL_MAX = 560
const RAIL_DEFAULT = 280
const RAIL_STORAGE_KEY = 'b4no.admin.formula.railWidth'

const storeRailWidth = (n) => {
  try { window.localStorage.setItem(RAIL_STORAGE_KEY, String(Math.round(n))) } catch { /* ignore */ }
}

const clampRail = (n) => Math.min(RAIL_MAX, Math.max(RAIL_MIN, n))

const readStoredRailWidth = () => {
  try {
    const raw = window.localStorage.getItem(RAIL_STORAGE_KEY)
    // The null check matters: Number(null) is 0, which IS finite, so without it
    // a first-time user got clamped to RAIL_MIN instead of RAIL_DEFAULT — the
    // rail opened narrower than the 260px that prompted this whole change.
    if (raw == null || raw === '') return RAIL_DEFAULT
    const n = Number(raw)
    if (!Number.isFinite(n)) return RAIL_DEFAULT
    return clampRail(n)
  } catch {
    // Private mode / blocked storage — the feature degrades to a fixed width.
    return RAIL_DEFAULT
  }
}

/**
 * Formula tab — the data-engineering surface for the selected KPI: which view
 * and columns feed it, and the SQL expression.
 *
 * The left rail exists so that authoring a formula no longer requires direct
 * SQL Server access. Views come from a curated catalog rather than being typed
 * from memory, and their columns are listed live, draggable into either a data
 * source field or the SQL editor at the caret.
 *
 * The SQL must still validate before it can be saved, so a typo can no longer
 * silently break the dashboard.
 */
export default function Beacon4PerformanceNetworkOverviewAdminFormulaPanel({ config, onSaved, notify }) {
  const dispatch       = useDispatch()
  const validation     = useSelector(selectAdminValidation)
  const saving         = useSelector(selectAdminSaving)
  const catalog        = useSelector(selectAdminCatalog)
  const columnsMap     = useSelector(selectAdminCatalogColumnsMap)
  const columnsLoading = useSelector(selectAdminCatalogLoading)
  const sqlFunctions   = useSelector(selectAdminSqlFunctions)

  const [form, setForm]           = useState(config)
  const [dirty, setDirty]         = useState(false)
  const [catalogId, setCatalogId] = useState(null)
  const [colFilter, setColFilter] = useState('')
  const [railWidth, setRailWidth] = useState(readStoredRailWidth)
  const [autoFilled, setAutoFilled] = useState(null)   // { detected, empty } | null

  // The textarea DOM node, plus the last known caret position. The caret has to
  // be remembered on blur: clicking a chip moves focus out of the textarea, so
  // by the time the click handler runs the live selection is already gone.
  const sqlRef   = useRef(null)
  const caretRef = useRef({ start: null, end: null })
  // Catalog id whose columns have already been requested, to dedupe the fetch
  // between onPickView and the effect that watches catalogId.
  const fetchedForRef = useRef(null)

  const rememberCaret = useCallback(() => {
    const el = sqlRef.current
    if (!el) return
    caretRef.current = { start: el.selectionStart, end: el.selectionEnd }
  }, [])

  // ── Rail resize ───────────────────────────────────────────────────────────
  // Pointer events with capture rather than window mousemove/mouseup.
  //
  // Capture keeps the events coming to the handle even when the cursor outruns
  // the strip, and — the reason it matters here — pointercancel plus the effect
  // below guarantee the drag is torn down if the panel unmounts mid-drag.
  // Closing this fullscreen dialog with Esc while still holding the button used
  // to leave listeners on window and, worse, body.userSelect = 'none' and
  // body.cursor = 'col-resize' stuck for the rest of the session.
  const dragRef = useRef(null)   // { startX, startW, frame } while dragging

  const endDrag = useCallback(() => {
    const d = dragRef.current
    if (!d) return
    if (d.frame) cancelAnimationFrame(d.frame)
    dragRef.current = null
    document.body.style.userSelect = ''
    document.body.style.cursor = ''
  }, [])

  // Safety net for unmount mid-drag — nothing else restores the body styles.
  useEffect(() => endDrag, [endDrag])

  const onRailResizeStart = useCallback((e) => {
    e.preventDefault()
    e.currentTarget.setPointerCapture?.(e.pointerId)
    dragRef.current = { startX: e.clientX, startW: railWidth, frame: 0 }
    // Without these the drag selects whatever text it passes over, and the
    // cursor flickers back to a caret whenever it leaves the handle.
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
  }, [railWidth])

  const onRailResizeMove = useCallback((e) => {
    const d = dragRef.current
    if (!d || d.frame) return
    const clientX = e.clientX
    // One state update per frame instead of one per event: the rail re-renders
    // the whole column list, and a fast drag fires far more often than 60Hz.
    d.frame = requestAnimationFrame(() => {
      d.frame = 0
      setRailWidth(clampRail(d.startW + (clientX - d.startX)))
    })
  }, [])

  const onRailResizeEnd = useCallback((e) => {
    const d = dragRef.current
    if (!d) return
    const next = clampRail(d.startW + (e.clientX - d.startX))
    endDrag()
    setRailWidth(next)
    // Committed on release, so a drag is one write instead of hundreds.
    storeRailWidth(next)
  }, [endDrag])

  const applyRailWidth = useCallback((n) => {
    const next = clampRail(n)
    setRailWidth(next)
    storeRailWidth(next)
  }, [])

  // Keyboard path, so the rail isn't mouse-only.
  const onRailResizeKey = useCallback((e) => {
    const step = e.shiftKey ? 40 : 12
    let next = null
    if (e.key === 'ArrowLeft')  next = railWidth - step
    if (e.key === 'ArrowRight') next = railWidth + step
    if (e.key === 'Home')       next = RAIL_DEFAULT
    if (next == null) return
    e.preventDefault()
    applyRailWidth(next)
  }, [railWidth, applyRailWidth])

  // ── Load catalog and palette ──────────────────────────────────────────────
  // Fetched unfiltered and narrowed by tech on the client. The catalog lives in
  // shared slice state and the metadata dialog reads the same key, so fetching a
  // tech-filtered list here would be overwritten the moment that dialog opened —
  // and this panel would silently start offering other technologies' views.
  useEffect(() => {
    dispatch(fetchAdminCatalog(undefined))
    dispatch(fetchAdminSqlFunctions())
  }, [dispatch])

  // ── Reset when a different KPI is selected ────────────────────────────────
  useEffect(() => {
    setForm(config)
    setDirty(false)
    setColFilter('')
    setAutoFilled(null)
    // Cleared rather than left in place: the resolve effect below bails out
    // early for a KPI with no view, which would otherwise leave the previous
    // KPI's catalog id in state and list the wrong columns.
    setCatalogId(null)
    caretRef.current = { start: null, end: null }
    fetchedForRef.current = null
    dispatch(clearValidation())
    // Keyed on the KPI id, not the config object.
    //
    // Saving triggers a configs refetch, which hands this component a new object
    // with the same id. With `config` as the dependency, that counted as a KPI
    // switch: catalogId was nulled, and the effect below that recovers it only
    // watches [form.viewName, catalog] — neither of which changed — so it never
    // re-ran. The panel was left with an empty column rail, a false "not in the
    // catalog" warning and a dead Validate button after every save.
  }, [config?.id, dispatch])   // eslint-disable-line react-hooks/exhaustive-deps

  // The saved KPI stores a view name, not a catalog id, so the id has to be
  // recovered by matching once the catalog arrives. A saved view that is no
  // longer in the catalog leaves this null — the field then shows the stale
  // name and the user is nudged to pick a current one.
  useEffect(() => {
    if (!form?.viewName || !catalog?.length) return
    const match = catalog.find(e => e.viewName?.toLowerCase() === form.viewName.toLowerCase())
    setCatalogId(match?.id ?? null)
  }, [form?.viewName, catalog])

  // Columns follow the selected catalog entry. Guarded by a ref so a selection
  // made through onPickView (which fetches them itself, to drive auto-fill)
  // doesn't fire a second identical request.
  useEffect(() => {
    if (catalogId == null || fetchedForRef.current === catalogId) return
    fetchedForRef.current = catalogId
    dispatch(fetchAdminCatalogColumns(catalogId))
  }, [catalogId, dispatch])

  const selectedEntry = useMemo(
    () => catalog?.find(e => e.id === catalogId) ?? null,
    [catalog, catalogId],
  )

  // Only views matching this KPI's technology, plus the ones marked as applying
  // to every technology (tech left null in the catalog).
  const techOptions = useMemo(() => {
    const tech = config?.tech
    if (!tech) return catalog ?? []
    return (catalog ?? []).filter(e => !e.tech || e.tech === tech)
  }, [catalog, config?.tech])

  // Columns for the view this panel is showing, and only that one. Reading the
  // map by id keeps the metadata dialog's own column fetches from displacing
  // what is rendered here.
  const catalogColumns = useMemo(
    () => (catalogId == null ? [] : columnsMap?.[catalogId] ?? []),
    [columnsMap, catalogId],
  )

  // A selected view whose column list came back empty. Array.isArray is the test
  // for "a request has already answered for this id" — the reducer writes the key
  // only on fulfilled/rejected, never on pending.
  const columnsUnavailable = catalogId != null && !columnsLoading
    && Array.isArray(columnsMap?.[catalogId]) && catalogColumns.length === 0

  const knownButUncatalogued = !!form?.viewName && catalogId == null && catalog?.length > 0

  // ── Mutations ─────────────────────────────────────────────────────────────
  const touch = useCallback(() => {
    setDirty(true)
    dispatch(clearValidation())
  }, [dispatch])

  const set = (k) => (e) => {
    setForm(f => ({ ...f, [k]: e.target.value }))
    touch()
  }

  const setField = useCallback((k, v) => {
    setForm(f => ({ ...f, [k]: v }))
    touch()
  }, [touch])

  // ── View selection + auto-fill ────────────────────────────────────────────
  const onPickView = async (entry) => {
    setCatalogId(entry?.id ?? null)
    setColFilter('')
    setAutoFilled(null)
    setForm(f => ({ ...f, viewName: entry?.viewName ?? '' }))
    touch()

    if (!entry) {
      fetchedForRef.current = null
      return
    }

    fetchedForRef.current = entry.id
    const res  = await dispatch(fetchAdminCatalogColumns(entry.id))
    const cols = Array.isArray(res?.payload) ? res.payload : []
    if (!cols.length) {
      // A 404 here means the catalog row was deactivated, or the object behind
      // it no longer exists. Say so — otherwise the rail just looks empty and
      // the user has no idea why.
      notify?.(`No columns available for ${entry.viewName}. It may have been removed from the catalog.`, 'warning')
      return
    }

    const { patch, detected, empty } = computeAutoFill(form, cols)
    setForm(f => ({ ...f, viewName: entry.viewName, ...patch }))
    setAutoFilled({ detected, empty })
  }

  // ── Token insertion ───────────────────────────────────────────────────────
  const insertIntoSql = useCallback((token, caretOffset = null) => {
    const el      = sqlRef.current
    const current = form?.sqlExpression ?? ''
    const live    = el && document.activeElement === el
    const start   = live ? el.selectionStart : (caretRef.current.start ?? current.length)
    const end     = live ? el.selectionEnd   : (caretRef.current.end   ?? start)

    const { value: next, caret } = spliceToken(current, start, end, token, caretOffset)
    setForm(f => ({ ...f, sqlExpression: next }))
    touch()

    caretRef.current = { start: caret, end: caret }

    // Deferred so the controlled value has been flushed to the DOM before the
    // selection is restored; setting it earlier would be overwritten by React.
    requestAnimationFrame(() => {
      const node = sqlRef.current
      if (!node) return
      node.focus()
      node.setSelectionRange(caret, caret)
    })
  }, [form?.sqlExpression, touch])

  const onDragToken = (token) => (e) => {
    e.dataTransfer.setData(DRAG_MIME, token)
    e.dataTransfer.effectAllowed = 'copy'
  }

  const allowDrop = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' }

  const onDropIntoSql = (e) => {
    e.preventDefault()
    const token = e.dataTransfer.getData(DRAG_MIME)
    if (!token) return

    // Read the caret the browser moved during the drag BEFORE focusing the
    // textarea — focus() restores the pre-drag selection and would undo it.
    // Browsers that don't track the caret while dragging over an unfocused
    // textarea (Firefox) fall back to the last remembered position, which is
    // the best guess available.
    const el = sqlRef.current
    if (el && document.activeElement !== el && typeof el.selectionStart === 'number') {
      caretRef.current = { start: el.selectionStart, end: el.selectionEnd }
    }
    insertIntoSql(token)
  }

  const onDropIntoField = (key) => (e) => {
    e.preventDefault()
    const token = e.dataTransfer.getData(DRAG_MIME)
    if (token) setField(key, token)
  }

  const onClickFunction = (fn) => () => {
    const { text, caret } = functionInsertion(fn)
    insertIntoSql(text, caret)
  }

  // ── Validation + save ─────────────────────────────────────────────────────
  // Sends catalogId rather than a free-text view name: the server resolves the
  // view itself, which keeps this endpoint from being usable to probe for
  // objects outside the catalog.
  // viewName is sent alongside catalogId purely so the server can tell "no
  // source picked" apart from "that source isn't in the catalog" and return the
  // matching message. It is ignored whenever catalogId is set, and on its own it
  // only resolves for views that are in the catalog anyway.
  const onValidate = () => {
    dispatch(validateAdminSql({
      catalogId,
      viewName: form.viewName || null,
      sqlExpression: form.sqlExpression,
    }))
  }

  const validated = !!validation?.valid

  // A KPI saved against a view that was never catalogued can't be validated —
  // there is no catalog id to send. Blocking the save outright would leave it
  // permanently uneditable, including its column mapping and audit note, so the
  // validation gate is waived as long as the view itself hasn't changed. Any
  // edit that switches to a catalogued view goes back through validation.
  const viewUnchanged     = (form?.viewName ?? '') === (config?.viewName ?? '')
  const validationWaived  = knownButUncatalogued && viewUnchanged
  const canSave           = dirty && (validated || validationWaived) && !saving

  const onSave = async () => {
    // catalogId is deliberately component state, not part of `form`, so it is
    // never posted — kpi_config stores the view name, not the catalog id.
    const { id, ...dto } = { ...form, note: form.note || 'Formula updated via admin' }
    const res = await dispatch(updateAdminConfig({ id: config.id, dto }))
    if (res?.error) notify?.('Could not save formula.', 'error')
    else {
      notify?.('Formula saved.', 'success')
      setDirty(false)
      onSaved?.(dto)
    }
  }

  // ── Derived ───────────────────────────────────────────────────────────────
  const visibleColumns = useMemo(() => {
    const q = colFilter.trim().toLowerCase()
    if (!q) return catalogColumns
    return catalogColumns.filter(c => c.columnName.toLowerCase().includes(q))
  }, [catalogColumns, colFilter])

  const missingSet = useMemo(
    () => new Set((validation?.missingColumns ?? []).map(c => c.toLowerCase())),
    [validation],
  )

  return (
    <Stack direction="row" spacing={0} alignItems="flex-start">

      {/* ── Schema rail ───────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, width: railWidth, flexShrink: 0, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1.5 }}>Data source</Typography>

        <Autocomplete
          size="small"
          options={techOptions}
          value={selectedEntry}
          onChange={(_, v) => onPickView(v)}
          getOptionLabel={catalogLabel}
          isOptionEqualToValue={(o, v) => o.id === v?.id}
          renderOption={(props, o) => (
            <Box component="li" {...props} key={o.id} sx={{ display: 'block !important' }}>
              <Typography sx={{ fontSize: 12 }}>{catalogLabel(o)}</Typography>
              {o.description?.trim() && (
                <Typography sx={{ fontSize: 10, color: 'text.secondary', fontFamily: 'monospace' }}>
                  {o.viewName}
                </Typography>
              )}
            </Box>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Source view" placeholder="Choose a view" />
          )}
        />

        {knownButUncatalogued && (
          <Alert severity="warning" sx={{ mt: 1, fontSize: 10, py: 0 }}>
            <strong>{form.viewName}</strong> is not in the catalog. Its columns can&apos;t be
            listed and the SQL can&apos;t be validated — pick a view from the list.
          </Alert>
        )}

        {/* Columns */}
        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, flex: 1 }}>
            Columns{catalogColumns.length > 0 && ` (${catalogColumns.length})`}
          </Typography>
          {columnsLoading && <CircularProgress size={12} />}
        </Stack>

        {catalogColumns.length > 8 && (
          <TextField
            size="small" fullWidth placeholder="Filter columns"
            value={colFilter} onChange={(e) => setColFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14 }} /></InputAdornment>
              ),
            }}
            sx={{ mb: 1, '& .MuiInputBase-input': { fontSize: 11 } }}
          />
        )}

        {!columnsLoading && catalogColumns.length === 0 && !columnsUnavailable && (
          <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
            Choose a source view to list its columns.
          </Typography>
        )}

        {/* Distinct from the message above: a view IS selected, it just has no
            columns to offer. Saying "choose a source view" here would be wrong. */}
        {columnsUnavailable && (
          <Alert severity="warning" sx={{ fontSize: 10, py: 0 }}>
            No columns available for this view. It may have been removed from the catalog.
          </Alert>
        )}

        <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
          {visibleColumns.map(c => (
            <Tooltip key={c.columnName} title="Drag into a field or the SQL, or click to insert" placement="right">
              <Box
                draggable
                onDragStart={onDragToken(c.columnName)}
                onClick={() => insertIntoSql(c.columnName)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  px: 0.75, py: 0.5, mb: 0.5, cursor: 'grab',
                  border: '1px solid', borderRadius: 1,
                  borderColor: missingSet.has(c.columnName.toLowerCase()) ? 'error.main' : 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <DragIndicatorIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                <Typography sx={{ fontSize: 11, fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>
                  {c.columnName}
                </Typography>
                <Typography sx={{ fontSize: 9, color: 'text.secondary' }}>{c.dataType}</Typography>
              </Box>
            </Tooltip>
          ))}
          {!columnsLoading && catalogColumns.length > 0 && visibleColumns.length === 0 && (
            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>No column matches.</Typography>
          )}
        </Box>

        {/* Function palette — served by the API from the validator's own
            allowlist, so it cannot offer something validation would reject. */}
        {sqlFunctions.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1 }}>Functions</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {sqlFunctions.map(fn => (
                <Chip
                  key={fn} label={fn} size="small" variant="outlined"
                  draggable
                  onDragStart={onDragToken(functionDragText(fn))}
                  onClick={onClickFunction(fn)}
                  sx={{ height: 20, fontSize: 10, fontFamily: 'monospace', cursor: 'grab' }}
                />
              ))}
            </Box>
          </>
        )}
      </Paper>

      {/* ── Resize handle ─────────────────────────────────────────────────── */}
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize the data source panel"
        aria-valuenow={Math.round(railWidth)}
        aria-valuemin={RAIL_MIN}
        aria-valuemax={RAIL_MAX}
        tabIndex={0}
        onPointerDown={onRailResizeStart}
        onPointerMove={onRailResizeMove}
        onPointerUp={onRailResizeEnd}
        onPointerCancel={endDrag}
        onKeyDown={onRailResizeKey}
        onDoubleClick={() => applyRailWidth(RAIL_DEFAULT)}
        title="Drag to resize · double-click to reset"
        sx={{
          alignSelf: 'stretch', flexShrink: 0,
          // 14px of grab area. The negative margins this used to carry were
          // silently dead: Stack's own `& > * + * { margin-left }` rule wins on
          // specificity, so they never applied and the gap was 38px.
          width: 14, borderRadius: 1,
          cursor: 'col-resize', touchAction: 'none',
          // Invisible until hovered: a permanent divider between two bordered
          // Papers reads as clutter, but there has to be something to aim at.
          bgcolor: 'transparent',
          transition: 'background-color 120ms',
          '&:hover, &:focus-visible': { bgcolor: 'primary.main', opacity: 0.35 },
          '&:focus-visible': { outline: 'none' },
        }}
      />

      {/* ── Editor ────────────────────────────────────────────────────────── */}
      {/* Stack spacing is 0 so the handle itself provides the gutter; without
          that the handle sat inside a 38px gap and the geometry drifted. */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Column mapping</Typography>
            {autoFilled && (
              <Chip
                icon={<AutoFixHighIcon sx={{ fontSize: 12 }} />}
                label={autoFillLabel(autoFilled)}
                size="small" color={autoFilled.empty === 0 ? 'success' : 'default'}
                sx={{ height: 18, fontSize: 10 }}
              />
            )}
          </Stack>

          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5}>
            {COLUMN_FIELDS.map(f => (
              <TextField
                key={f.key} label={f.label} size="small"
                value={form[f.key] ?? ''} onChange={set(f.key)}
                onDragOver={allowDrop} onDrop={onDropIntoField(f.key)}
                placeholder="drop a column"
                sx={{ width: FIELD_WIDTHS[f.key] }}
              />
            ))}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1 }}>SQL expression</Typography>
            <Button size="small" variant="outlined" onClick={onValidate} disabled={catalogId == null}>
              Validate
            </Button>
          </Stack>

          <TextField
            fullWidth multiline minRows={6} maxRows={16}
            inputRef={sqlRef}
            value={form.sqlExpression ?? ''} onChange={set('sqlExpression')}
            onSelect={rememberCaret} onBlur={rememberCaret}
            onDragOver={allowDrop} onDrop={onDropIntoSql}
            placeholder="e.g. 100.0 * SUM(ISNULL(NUM,0)) / NULLIF(SUM(ISNULL(DEN,0)),0)"
            sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 11 } }}
          />
          <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 0.5 }}>
            Drop a column or function to insert it at the cursor. Clicking one inserts it too.
          </Typography>

          {validation && (
            <Alert
              severity={validation.valid ? 'success' : 'error'}
              icon={validation.valid ? <CheckCircleIcon fontSize="small" /> : <ErrorIcon fontSize="small" />}
              sx={{ mt: 1, fontSize: 11, '& .MuiAlert-message': { width: '100%' } }}
            >
              {validation.valid ? (
                <>
                  <strong>SQL is valid.</strong>
                  {validation.detectedColumns?.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      Detected columns:{' '}
                      {validation.detectedColumns.map(c =>
                        <Chip key={c} label={c} size="small" sx={{ height: 18, fontSize: 10, mr: 0.5, mt: 0.25 }} />)}
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <strong>Validation failed:</strong>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {(validation.errors ?? []).map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                  {validation.missingColumns?.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      Missing in view:{' '}
                      {validation.missingColumns.map(c =>
                        <Chip key={c} label={c} size="small" color="error" sx={{ height: 18, fontSize: 10, mr: 0.5, mt: 0.25 }} />)}
                    </Box>
                  )}
                </>
              )}
            </Alert>
          )}

          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 2 }}>
            <TextField
              label="Change note (audit log)" size="small" sx={{ flex: 1 }}
              value={form.note ?? ''} onChange={set('note')}
              placeholder="Optional — describe what you changed"
            />
            <Button variant="contained" onClick={onSave} disabled={!canSave}>
              {saving ? 'Saving…' : 'Save formula'}
            </Button>
          </Stack>
          {dirty && !validated && !validationWaived && (
            <Typography sx={{ fontSize: 10, color: 'warning.main', mt: 0.5 }}>
              Validate the SQL before saving.
            </Typography>
          )}
          {dirty && validationWaived && (
            <Typography sx={{ fontSize: 10, color: 'warning.main', mt: 0.5 }}>
              Saving without validation — this KPI&apos;s view isn&apos;t in the catalog.
            </Typography>
          )}
        </Paper>
      </Box>
    </Stack>
  )
}
